// store.ts
import { create } from "zustand";
import * as Yup from "yup";
// @ts-ignore
import instance from "../api/instance";
import Cookies from "js-cookie";
import {
  companyDetailsPayloadFromLoginData,
  setAuthToken,
  setCompanyDetails,
  setJobseekerId,
  setNewUser,
  setUser,
} from "../redux/reducers/authReducer";
import { Dispatch } from "redux";
import { toast } from "react-toastify";
import { User } from "./userStore";
import { sendLoginOtp } from "./apis/sendLoginOtp";
import { verifyLoginOtp } from "./apis/verifyLoginOtp";
// Per-function import: the full validator package is ~130 KB in the entry chunk.
import validatorIsEmail from "validator/es/lib/isEmail";
// @ts-ignore
import { emailRegex } from "../utils/constants";
import { store } from "../redux/store";
import {
  extractJobIdFromPath, getDefaultRouteForRole, getInvitedMemberOnboardingUrl, getInvitationOnboardingUrl, isInvitedMember, resolvePostLoginRedirect, ROLE_IDS } from "../constants/roleConfig";

/** Survives OTP success (in-memory store is cleared) until onboarding consumes it. */
export const INVITE_MAIL_TOKEN_SESSION_KEY = "haire_invite_mail_token";

interface LoginCodeFormData {
  email: string;
}

interface FormErrors {
  [key: string]: string | undefined;
}

const WALKTHROUGH_NEW_REG_KEY = "_wt_new_reg";

interface StoreState {
  formData: LoginCodeFormData;
  formErrors: FormErrors;
  /** From invite / private links (`?mailtoken=`) — set before redirect to `/new-login-with-verification-code`; cleared after successful OTP login. */
  mailToken: string | null;
  setMailToken: (token: string | null) => void;
  setFormData: (name: keyof LoginCodeFormData, value: string) => void;
  validateForm: () => Promise<boolean>;
  validateField: (name: keyof LoginCodeFormData, value: string) => void;
  error: string | null;
  loading: boolean;
  modalOpen: boolean;
  initialModalOpen: boolean;
  loginWithCode: (
    email: string,
    onOtpSent: Function,
    redirectUrl?: string,
    registerUser?: Function,
    retryCount?: number
  ) => void;
  verifyLoginOtp: (
    email: string,
    otp: number,
    dispatch: Dispatch,
    navigate: Function,
    redirectUrl?: string,
    isCopyLinkFlow?: boolean,
    sourceInvitation?: boolean
  ) => Promise<boolean>;
  loginWithUserId: (
    email: string,
    userId: string,
    dispatch: Dispatch,
    navigate: Function,
    redirectUrl?: string
  ) => Promise<boolean>;
  resendLoginOtp: (email: string) => void;
  openModal: () => void;
  closeModal: () => void;
  initialOpenModal: () => void;
  initialCloseModal: () => void;
  resetFormData: () => void;
}

const isEmail = (email: string) => {
  return validatorIsEmail(email) && emailRegex.test(email);
};

const schema = Yup.object<LoginCodeFormData>({
  email: Yup.string()
    // .email("Invalid email address")
    // .matches(
    //   // Regex for more stringent email validation
    //   /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/,
    //   "Please use valid email address"
    // )
    .required("Email is required")
    .test("is-valid-email", "Invalid email address", (value) =>
      isEmail(value ?? "")
    ),
});

const loginCodeStore = create<StoreState>((set, get) => ({
  formData: {
    email: "",
  },
  formErrors: {},
  mailToken: null,
  setMailToken: (token) => {
    const normalized =
      token == null || String(token).trim() === ""
        ? null
        : String(token).trim();
    if (normalized === null) {
      try {
        sessionStorage.removeItem(INVITE_MAIL_TOKEN_SESSION_KEY);
      } catch {
        /* ignore */
      }
    }
    set({ mailToken: normalized });
  },
  setFormData: (name, value) =>
    set((state) => ({
      formData: {
        ...state.formData,
        [name]: name === "email" ? String(value).toLowerCase().trim() : value,
      },
    })),
  resetFormData: () => set({ formData: { email: "" } }),
  validateForm: async () => {
    try {
      await schema.validate(get().formData, { abortEarly: false });
      set({ formErrors: {} });
      return true;
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const errors = err.inner.reduce<FormErrors>(
          (acc, { path, message }) => {
            if (path) {
              acc[path as keyof LoginCodeFormData] = message;
            }
            return acc;
          },
          {}
        );
        set({ formErrors: errors });
      }
      return false;
    }
  },
  validateField: async (name: keyof LoginCodeFormData, value: string) => {
    try {
      await schema.validateAt(name, { ...get().formData, [name]: value });
      set({ formErrors: { ...get().formErrors, [name]: undefined } });
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        set({ formErrors: { ...get().formErrors, [name]: error.message } });
      }
    }
  },
  setFormErrors: (errors: any) =>
    set((state) => ({
      formErrors: { ...state.formErrors, ...errors },
    })),
  error: null,
  loading: false,
  modalOpen: false,
  initialModalOpen: false,
  loginWithCode: async (email, onOtpSent, redirectUrl, registerUser, retryCount = 0) => {
    const maxRetries = 1; // Allow one retry attempt
    
    // Set loading to true only on first attempt
    if (retryCount === 0) {
      set({ loading: true });
    }
    
    try {
      const message = await sendLoginOtp(email);
      toast.success(message);
      sessionStorage.setItem("otp_email", email);
      onOtpSent();
      // Only reset loading on success
      set({ loading: false });
    } catch (error: any) {
      // Prefer API response body over raw Axios message (e.g. "Request failed with status code 400")
      const apiMessage =
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        error?.response?.data?.error;
      let errorMessage =
        (typeof apiMessage === "string" ? apiMessage : null) ||
        (error instanceof Error ? error.message : null) ||
        (typeof error === "string" ? error : null) ||
        "Failed to send login verification code.";
      let isNetworkError = false;
      
      // Check if it's a network error (no response from server)
      if (!error?.response) {
        isNetworkError = true;
        errorMessage = "Network error. Please check your connection and try again.";
        if (error?.message) {
          errorMessage = error.message;
        }
      } else {
        // Check if error message indicates network issue
        const errorMsgLower = errorMessage.toLowerCase();
        if (errorMsgLower.includes('network') || 
            errorMsgLower.includes('timeout') || 
            errorMsgLower.includes('fetch') ||
            errorMsgLower.includes('failed to fetch')) {
          isNetworkError = true;
        }
      }
      
      // Retry logic: if it's a network error and we haven't exceeded max retries, retry immediately
      if (isNetworkError && retryCount < maxRetries) {
        // Suppress error toast on retry
        toast.dismiss();
        // Retry immediately without delay - loader continues running
        return get().loginWithCode(email, onOtpSent, redirectUrl, registerUser, retryCount + 1);
      }
      
      if (errorMessage === "No user found with this email" && registerUser) {
        try {
          // Scope this transient flag to the registering email so it
          // cannot leak and open the walkthrough for a different user.
          localStorage.setItem(
            WALKTHROUGH_NEW_REG_KEY,
            JSON.stringify({
              email: email.trim().toLowerCase(),
              createdAt: Date.now(),
            }),
          );
          await registerUser();
          // Reset loading after registerUser succeeds
          set({ loading: false });
          return;
        } catch (registerError) {
          // If registerUser fails, clear the flag so it doesn't stick
          localStorage.removeItem(WALKTHROUGH_NEW_REG_KEY);
          set({ loading: false });
          throw registerError;
        }
      }
      
      set({ error: errorMessage });
      
      // Only show toast if server responded (API error), not for network errors on retry
      if (error?.response || (!isNetworkError || retryCount >= maxRetries)) {
        toast.error(errorMessage);
      }
      
      // Only reset loading after all retries are exhausted
      set({ loading: false });
      
      // Re-throw error so caller can handle it properly
      throw error;
    }
  },
  verifyLoginOtp: async (email, otp, dispatch, navigate, redirectUrl, isCopyLinkFlow?: boolean, sourceInvitation?: boolean) => {
    set({ loading: true });
    try {
      // Use Redux redirectUrl when not passed (e.g. invitation flow from OTP page)
      const effectiveRedirectUrl = redirectUrl ?? store.getState()?.auth?.redirectUrl ?? null;

      const loginResponse = await verifyLoginOtp(email, otp);

      const userDetails: Record<string, unknown> = {
        user_id: loginResponse.userId,
        email: email,
        key: loginResponse.userId,
      };

      // Send the user details to your backend
      const response = await instance.post(
        "/accounts/social-login/",
        userDetails
      );

      // Example of handling the response
      // Token is now stored in sessionStorage via redux-persist
      dispatch(setAuthToken(response.data.data.token));
      dispatch(setNewUser(response.data.data.is_new_user));
      dispatch(setJobseekerId(response.data.data.jobseeker_id));
      {
        const cdPayload = companyDetailsPayloadFromLoginData(
          response.data.data,
        );
        if (cdPayload) dispatch(setCompanyDetails(cdPayload));
      }

      const user: User = {
        user_id: response.data.data.user.id,
        job_seeker_id: response.data.data.jobseeker_id,
        full_name: response.data.data.user.full_name,
        email: email,
        type: 3,
        country_code: null,
        phone: response.data.data.contact,
        key: response.data.data.user.id,
        role: response.data.data.user.role,
        registered_company_name:
          response.data.data.user.registered_company_name,
        registered_industry_name:
          response.data.data.user.registered_industry_name,
        company_id: response.data.data.company_id ?? response.data.data.user?.company_id ?? null,
        languages: null,
        websites: null,
        profile_pic: null,
        designation: null,
        dob: null,
        gender: null,
        address: null,
        introduction: null,
        resume: response.data.data.user.resume,
        resume_json: null,
        is_active: null,
        is_deleted: null,
        profile_completed: response.data.data.profile_completed,
        token: response.data.data.token,
        companyName: null,
        verified: true,
        first_name: response.data.data.user.first_name ?? null,
        last_name: response.data.data.user.last_name ?? null,
        groups: response.data.data.user.groups ?? null,
      };

      await dispatch(setUser(user));
      {
        const mt = get().mailToken;
        if (mt) {
          try {
            sessionStorage.setItem(INVITE_MAIL_TOKEN_SESSION_KEY, mt);
          } catch {
            /* ignore */
          }
        }
      }
      set({ mailToken: null });

      // Role 1/5: use centralized redirect (checks full_name, groups, invitation flow)
      if (user.role === 1 || user.role === 5) {
        resolvePostLoginRedirect(
          { ...user, is_new_user: response.data.data.is_new_user },
          effectiveRedirectUrl ?? undefined,
          navigate,
        );
        return true;
      }

      // Role 3 (User): must use resolvePostLoginRedirect — getDefaultRouteForRole(3) is /dashboard and would skip /user-role on repeat login.
      if (user.role === ROLE_IDS.USER) {
        resolvePostLoginRedirect(
          { ...user, is_new_user: response.data.data.is_new_user },
          effectiveRedirectUrl ?? undefined,
          navigate,
        );
        return true;
      }

      if (user.role === ROLE_IDS.APPLICANT) {
        // If job_seeker_id doesn't exist, user needs to complete resume first
        if (!user.job_seeker_id) {
          // Extract job ID from redirect URL if it's a job-related route
          const jobId = extractJobIdFromPath(effectiveRedirectUrl);
          
          navigate("/resume-builder", {
            state: {
              fromCopyLink: jobId ? true : false,
              jobId: jobId
            }
          });
        } else if (
          effectiveRedirectUrl &&
          !effectiveRedirectUrl.includes("from=pricing")
        ) {
          // Jobseeker must not be sent to the recruiter pricing/billing page
          // captured from a public pricing CTA (verification-code → social-login
          // path). Other redirects (e.g. job page) are preserved.
          navigate(effectiveRedirectUrl);
        } else {
          navigate("/applicant-dashboard");
        }
      } else {
        // Role 4 (client) or other roles with direct dashboard → never go to /user-role
        const defaultRoute = getDefaultRouteForRole(user.role);
        if (defaultRoute) {
          toast.success("Logged in successfully");
          navigate(effectiveRedirectUrl || defaultRoute);
          return true;
        }
        // New user with role 0 or unknown
        // Only navigate to resume-builder if it's copy link flow (from lead form)
        // If they came from a client dashboard link (e.g. shared candidates email), send them there
        // Otherwise, use normal flow (go to /user-role)
        if (isCopyLinkFlow) {
          // Copy link flow (from lead form) - go to resume builder
          const state = store.getState();
          const stateRedirectUrl = state?.auth?.redirectUrl || redirectUrl;
          const jobId = extractJobIdFromPath(stateRedirectUrl);
          navigate("/resume-builder", {
            state: {
              fromCopyLink: jobId ? true : false,
              jobId: jobId
            }
          });
        } else if (effectiveRedirectUrl && effectiveRedirectUrl.includes("/client-dashboard")) {
          // Client clicked email link to view shared candidates; role may be 0 on first login
          toast.success("Logged in successfully");
          navigate(effectiveRedirectUrl);
        } else if (effectiveRedirectUrl && (effectiveRedirectUrl.includes("/onboarding") || effectiveRedirectUrl.includes("source=invitation"))) {
          // Invitation flow: send to company onboarding
          toast.success("Logged in successfully");
          navigate(effectiveRedirectUrl);
        } else {
          const onboardingUrl = getInvitedMemberOnboardingUrl(user.groups, user.email ?? email, user.profile_completed, user.role);
          if (onboardingUrl) {
            toast.success("Logged in successfully");
            navigate(onboardingUrl);
          } else {
            navigate("/user-role");
          }
        }
      }
      return true; // Indicate successful verification
    } catch (error: any) {
      // Ensure loading is reset even if error handling fails
      set({ loading: false });
      
      // Prefer API response body over raw Axios message (e.g. "Request failed with status code 400")
      const status = error?.response?.status;
      const apiMessage =
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        error?.response?.data?.error;
      let errorMessage =
        (typeof apiMessage === "string" ? apiMessage : null) ||
        (error instanceof Error ? error.message : null) ||
        (typeof error === "string" ? error : null) ||
        "Failed to verify login verification code.";

      if (
        typeof errorMessage === "string" &&
        errorMessage.startsWith("Request failed with status code")
      ) {
        errorMessage =
          status === 400
            ? "Verification Code is Invalid or Expired"
            : "Failed to verify login verification code.";
      }

      set({ error: errorMessage });
      if (status !== 400) {
        toast.error(errorMessage);
      }
      return false; // Indicate failed verification
    } finally {
      // Ensure loading is always reset, even if there was an error in catch block
      set({ loading: false });
    }
  },

  loginWithUserId: async (email, userId, dispatch, navigate, redirectUrl) => {
    set({ loading: true });
    try {
      const userDetails = {
        user_id: userId,
        email: email,
        key: userId,
      };

      // Send the user details to your backend
      const response = await instance.post(
        "/accounts/social-login/",
        userDetails
      );

      // Example of handling the response
      // Token is now stored in sessionStorage via redux-persist
      dispatch(setAuthToken(response.data.data.token));
      dispatch(setNewUser(response.data.data.is_new_user));
      dispatch(setJobseekerId(response.data.data.jobseeker_id));
      {
        const cdPayload = companyDetailsPayloadFromLoginData(
          response.data.data,
        );
        if (cdPayload) dispatch(setCompanyDetails(cdPayload));
      }

      const user: User = {
        user_id: response.data.data.user.id,
        job_seeker_id: response.data.data.jobseeker_id,
        full_name: response.data.data.user.full_name,
        email: email,
        type: 3,
        country_code: null,
        phone: response.data.data.contact,
        key: response.data.data.user.id,
        role: response.data.data.user.role,
        registered_company_name:
          response.data.data.user.registered_company_name,
        registered_industry_name:
          response.data.data.user.registered_industry_name,
        company_id: response.data.data.company_id ?? response.data.data.user?.company_id ?? null,
        languages: null,
        websites: null,
        profile_pic: null,
        designation: null,
        dob: null,
        gender: null,
        address: null,
        introduction: null,
        resume: response.data.data.user.resume,
        resume_json: null,
        is_active: null,
        is_deleted: null,
        profile_completed: response.data.data.profile_completed,
        token: response.data.data.token,
        companyName: null,
        verified: true,
        first_name: response.data.data.user.first_name ?? null,
        last_name: response.data.data.user.last_name ?? null,
        groups: response.data.data.user.groups ?? null,
      };

      await dispatch(setUser(user));
      {
        const mt = get().mailToken;
        if (mt) {
          try {
            sessionStorage.setItem(INVITE_MAIL_TOKEN_SESSION_KEY, mt);
          } catch {
            /* ignore */
          }
        }
      }
      set({ mailToken: null });

      resolvePostLoginRedirect(
        { ...user, is_new_user: response.data.data.is_new_user },
        redirectUrl,
        navigate,
      );
      return true; // Indicate successful verification
    } catch (error: any) {
      // Ensure loading is reset even if error handling fails
      set({ loading: false });

      // Prefer API response body over raw Axios message
      const apiMessage =
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        error?.response?.data?.error;
      const errorMessage =
        (typeof apiMessage === "string" ? apiMessage : null) ||
        (error instanceof Error ? error.message : null) ||
        (typeof error === "string" ? error : null) ||
        "Failed to login with user ID.";
      
      set({ error: errorMessage });
      toast.error(errorMessage);
      return false; // Indicate failed verification
    } finally {
      // Ensure loading is always reset, even if there was an error in catch block
      set({ loading: false });
    }
  },

  resendLoginOtp: async (email) => {
    set({ loading: true });
    try {
      const message = await sendLoginOtp(email);
      toast.success(message);
    } catch (error: any) {
      // Prefer API response body over raw Axios message
      const apiMessage =
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        error?.response?.data?.error;
      const errorMessage =
        (typeof apiMessage === "string" ? apiMessage : null) ||
        (error instanceof Error ? error.message : null) ||
        (typeof error === "string" ? error : null) ||
        "Failed to resend login verification code.";
      
      // Only show toast if server responded (API error), not for network errors
      if (error?.response) {
        toast.error(errorMessage);
      }
      set({ error: errorMessage });
      return false;
    } finally {
      // Ensure loading is always reset, even if there was an error in catch block
      set({ loading: false });
    }
  },
  openModal: () => set({ modalOpen: true }),
  closeModal: () => set({ modalOpen: false, error: null }),
  initialOpenModal: () => set({ initialModalOpen: true }),
  initialCloseModal: () => set({ initialModalOpen: false }),
}));

export default loginCodeStore;
